import type { ComponentProps, ReactNode } from "react";
import { Link } from "react-router-dom";

import { TenkoNavMain, type TenkoNavMainSection } from "@/shared/components/layout/TenkoNavMain";
import {
  TenkoNavSecondary,
  type TenkoNavSecondaryItem,
} from "@/shared/components/layout/TenkoNavSecondary";
import { TenkoNavUser } from "@/shared/components/layout/TenkoNavUser";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/shared/components/primitives/sidebar";

export type TenkoAppSidebarBrand = {
  label: ReactNode;
  to?: string;
  icon?: ReactNode;
};

export type TenkoAppSidebarUser = {
  name: string;
  email: string;
  avatar?: string;
};

export type TenkoAppSidebarOrganization = {
  name: string;
  avatar?: string;
};

export type TenkoAppSidebarLabels = {
  account: string;
  settings: string;
  logout: string;
  organization: string;
};

export type TenkoAppSidebarProps = {
  sections: TenkoNavMainSection[];
  footerItems?: TenkoNavSecondaryItem[];
  brand: TenkoAppSidebarBrand;
  user: TenkoAppSidebarUser;
  organization?: TenkoAppSidebarOrganization;
  navUserLabels: TenkoAppSidebarLabels;
  onAccount?: () => void;
  onSettings?: () => void;
  onLogout?: () => void;
} & Omit<ComponentProps<typeof Sidebar>, "children">;

export function TenkoAppSidebar({
  sections,
  footerItems,
  brand,
  user,
  organization,
  navUserLabels,
  onAccount,
  onSettings,
  onLogout,
  variant = "inset",
  ...props
}: TenkoAppSidebarProps) {
  return (
    <Sidebar variant={variant} {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" render={brand.to ? <Link to={brand.to} /> : <div />}>
              {brand.icon ? (
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                  {brand.icon}
                </div>
              ) : null}
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-bold">{brand.label}</span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent className="pb-4">
        <TenkoNavMain sections={sections} />
        {footerItems && footerItems.length > 0 ? (
          <TenkoNavSecondary items={footerItems} className="mt-auto" />
        ) : null}
      </SidebarContent>
      <SidebarFooter className="relative border-t border-dashed">
        <div className="absolute -top-[calc(--spacing(10)+1px)] left-0 w-full h-10 pointer-events-none bg-linear-to-t from-sidebar to-transparent"></div>
        <TenkoNavUser
          user={user}
          organization={organization}
          accountLabel={navUserLabels.account}
          settingsLabel={navUserLabels.settings}
          logoutLabel={navUserLabels.logout}
          organizationLabel={navUserLabels.organization}
          onAccount={onAccount}
          onSettings={onSettings}
          onLogout={onLogout}
        />
      </SidebarFooter>
    </Sidebar>
  );
}
